<template>
  <div>
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Membership discount
        </h4>
        <p class="kb-row-hint">
          A one-time lower price on this member's own membership for one parade year.
        </p>
      </div>
      <div
        v-if="openYears.length > 0"
        class="kb-row-aside"
      >
        <Button
          label="Give discount"
          icon="pi pi-tag"
          outlined
          severity="secondary"
          size="small"
          :disabled="loading"
          @click="openDialog()"
        />
      </div>

      <div
        v-if="error || loading || rows.length > 0"
        class="kb-row-body"
      >
        <Message
          v-if="error"
          severity="error"
          variant="simple"
          size="small"
          :closable="false"
        >
          {{ error }}
        </Message>

        <KbSkeletonRows
          v-else-if="loading && rows.length === 0"
          :rows="2"
          aside
          label="Loading discounts"
        />

        <!-- One entry per discount, hairlines between them -->
        <div
          v-if="rows.length > 0"
          class="kb-rows"
        >
          <div
            v-for="row in rows"
            :key="row.id"
            class="grid gap-1 py-2.5 first:pt-0 last:pb-0"
          >
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span class="kb-row-meta">{{ row.year }}</span>
              <span class="text-sm font-semibold text-kb-fg">{{ row.label }}</span>
              <span
                class="kb-tag"
                :class="memberDiscountTagClass(row)"
              >{{ memberDiscountStatusLabel(row) }}</span>
            </div>
            <p class="kb-row-hint">
              {{ rowSummary(row) }}
            </p>
            <p
              v-if="row.reason"
              class="kb-row-note"
            >
              Reason: {{ row.reason }}
            </p>
            <p class="kb-row-meta m-0">
              Given by {{ row.createdByName ?? 'an admin' }}<template v-if="row.createdAt">
                on {{ formatDate(row.createdAt) }}
              </template>
              <template v-if="row.status === 'revoked'">
                · Removed by {{ row.revokedByName ?? 'an admin' }}<template v-if="row.revokedAt">
                  on {{ formatDate(row.revokedAt) }}
                </template>
              </template>
            </p>
            <div
              v-if="row.status === 'available'"
              class="kb-row-actions mt-1"
            >
              <Button
                label="Change"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                outlined
                @click="openDialog(row)"
              />
              <Button
                label="Remove"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                @click="confirmRevoke(row)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="dialogVisible"
      modal
      :header="editing ? 'Change membership discount' : 'Give membership discount'"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :closable="!saving"
    >
      <div class="kb-form">
        <div
          v-if="!editing && openYears.length > 1"
          class="kb-field"
        >
          <label id="member-discount-year-label">Parade year</label>
          <SelectButton
            v-model="form.year"
            :options="openYears"
            :allow-empty="false"
            size="small"
            fluid
            aria-labelledby="member-discount-year-label"
          />
        </div>
        <p
          v-else
          class="kb-dialog-lead"
        >
          For the <strong>{{ form.year }}</strong> parade
        </p>

        <div class="kb-field">
          <label id="member-discount-type-label">Kind of discount</label>
          <SelectButton
            v-model="form.type"
            :options="TYPE_OPTIONS"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            size="small"
            fluid
            aria-labelledby="member-discount-type-label"
          />
          <small class="kb-field-hint">{{ MEMBER_DISCOUNT_TYPE_HELP[form.type] }}</small>
        </div>

        <!-- The price preview sits under the amount: neutral when the
             discount works, gold when it needs a look -->
        <div class="kb-field">
          <label
            :for="form.type === 'percent' ? 'member-discount-percent' : 'member-discount-fixed'"
          >{{ form.type === 'percent' ? 'Percentage off' : 'Price they pay' }}</label>
          <InputNumber
            v-if="form.type === 'percent'"
            v-model="form.percentOff"
            input-id="member-discount-percent"
            :min="0"
            :max="99.99"
            mode="decimal"
            :max-fraction-digits="2"
            suffix=" %"
            size="small"
            fluid
            :pt="{ pcInputText: { root: { 'autofocus': true, 'aria-describedby': 'member-discount-preview' } } }"
          />
          <InputNumber
            v-else
            v-model="form.fixedPriceDollars"
            input-id="member-discount-fixed"
            :min="0"
            :max="10000"
            mode="currency"
            currency="USD"
            locale="en-US"
            size="small"
            fluid
            :pt="{ pcInputText: { root: { 'autofocus': true, 'aria-describedby': 'member-discount-preview' } } }"
          />
          <small
            id="member-discount-preview"
            class="kb-field-hint"
            :class="{ '!text-kb-gold': preview.tone !== 'ok' }"
          >{{ preview.text }}</small>
        </div>

        <div class="kb-field">
          <label for="member-discount-reason">Reason</label>
          <Textarea
            id="member-discount-reason"
            v-model="form.reason"
            rows="2"
            size="small"
            fluid
            :maxlength="MAX_MEMBER_DISCOUNT_REASON_LENGTH"
            placeholder="Why this member gets a discount"
          />
        </div>

        <p class="kb-field-hint m-0">
          Used once: it applies to {{ memberName }}'s next membership purchase for {{ form.year }}, then it's spent.
          It only covers a membership they buy themselves. To give a membership for free, use Mark as paid instead.
        </p>

        <Message
          v-if="formError"
          severity="error"
          size="small"
          variant="simple"
          :closable="false"
        >
          {{ formError }}
        </Message>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          :disabled="saving"
          @click="dialogVisible = false"
        />
        <Button
          :label="editing ? 'Save' : 'Give discount'"
          :loading="saving"
          :disabled="!canSave"
          @click="submit"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { Member } from '~/shared/types/member.js'
import {
  MAX_MEMBER_DISCOUNT_REASON_LENGTH,
  MEMBER_DISCOUNT_TYPE_HELP,
  explainMemberDiscountPrice,
  memberDiscountLowersPrice,
  validateMemberDiscountTerms,
} from '~/shared/discounts/member.js'
import { memberDiscountStatusLabel, memberDiscountTagClass } from '~/composables/useMemberDiscounts.js'
import type { MemberDiscountRow, MemberDiscountType } from '~/shared/discounts/member.js'
import { formatCents } from '~/shared/bulk/discount.js'
import { formatDate } from '~/utils/dateUtils'

const props = defineProps<{
  member: Member
}>()

const TYPE_OPTIONS: Array<{ label: string, value: MemberDiscountType }> = [
  { label: 'Percentage off', value: 'percent' },
  { label: 'Fixed price', value: 'fixed' },
]

const toast = useToast()
const confirm = useConfirm()
const { paradeYear: localParadeYear } = useParadeYear()
const { rows, tiers, paradeYear: serverParadeYear, loading, error, refresh, save, revoke } = useMemberDiscounts()

const memberName = computed(() => props.member.name || 'this member')
const paradeYear = computed(() => serverParadeYear.value ?? String(localParadeYear.value))

/** Years that can still take a discount: no live or spent one already. */
const openYears = computed(() => {
  const current = Number(paradeYear.value)
  return [String(current), String(current + 1)]
    .filter(y => !rows.value.some(r => r.year === y && r.status !== 'revoked'))
})

async function load() {
  if (props.member.id) await refresh({ memberId: props.member.id })
}

onMounted(load)

function rowSummary(row: MemberDiscountRow): string {
  if (row.status === 'used') {
    const when = row.usedAt ? ` on ${formatDate(row.usedAt)}` : ''
    if (row.chargedUnitAmount !== null && row.regularUnitAmount !== null) {
      return `Used${when}: paid ${formatCents(row.chargedUnitAmount)} instead of ${formatCents(row.regularUnitAmount)}.`
    }
    return `Used${when}.`
  }
  if (row.status === 'revoked') return 'Removed. It no longer applies at checkout.'
  if (row.paidWithoutIt) return `They already have a ${row.year} membership, so this will not be used.`
  const tier = tiers.value[row.year] ?? null
  return `Applies to their next ${row.year} membership purchase. ${explainMemberDiscountPrice(row, tier?.unitAmount ?? null)}`
}

// ---- Give / change dialog ----

const dialogVisible = ref(false)
const editing = ref(false)
const saving = ref(false)
const formError = ref<string | null>(null)
const form = ref<{
  year: string
  type: MemberDiscountType
  percentOff: number | null
  fixedPriceDollars: number | null
  reason: string
}>({ year: '', type: 'percent', percentOff: null, fixedPriceDollars: null, reason: '' })

function openDialog(row?: MemberDiscountRow) {
  formError.value = null
  editing.value = Boolean(row)
  form.value = row
    ? {
      year: row.year,
      type: row.type,
      percentOff: row.percentOff,
      fixedPriceDollars: row.fixedPrice !== null ? row.fixedPrice / 100 : null,
      reason: row.reason,
    }
    : { year: openYears.value[0] ?? paradeYear.value, type: 'percent', percentOff: null, fixedPriceDollars: null, reason: '' }
  dialogVisible.value = true
}

const validation = computed(() => validateMemberDiscountTerms({
  type: form.value.type,
  percentOff: form.value.percentOff,
  fixedPrice: form.value.fixedPriceDollars !== null ? Math.round(form.value.fixedPriceDollars * 100) : null,
}))

const preview = computed<{ tone: 'ok' | 'warn', text: string }>(() => {
  const result = validation.value
  if (!result.ok) return { tone: 'warn', text: result.error }
  const tier = tiers.value[form.value.year] ?? null
  const lowers = tier !== null && memberDiscountLowersPrice(result.terms, tier.unitAmount)
  return { tone: lowers ? 'ok' : 'warn', text: explainMemberDiscountPrice(result.terms, tier?.unitAmount ?? null) }
})

const canSave = computed(() => validation.value.ok && form.value.reason.trim().length > 0 && !saving.value)

async function submit() {
  const result = validation.value
  if (!result.ok || !props.member.id) return
  saving.value = true
  formError.value = null
  try {
    await save({
      memberId: props.member.id,
      year: form.value.year,
      type: result.terms.type,
      percentOff: result.terms.percentOff,
      fixedPrice: result.terms.fixedPrice,
      reason: form.value.reason.trim(),
    })
    dialogVisible.value = false
    toast.add({ severity: 'success', summary: 'Discount saved', detail: `${memberName.value}'s ${form.value.year} membership discount is set`, life: 3000 })
    await load()
  }
  catch (err) {
    formError.value = err instanceof Error ? err.message : 'Failed to save the discount'
  }
  finally {
    saving.value = false
  }
}

function confirmRevoke(row: MemberDiscountRow) {
  confirm.require({
    message: `Remove ${memberName.value}'s ${row.year} discount (${row.label})? It will no longer apply at checkout.`,
    header: 'Remove Discount',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await revoke(row.memberId, row.year)
        toast.add({ severity: 'success', summary: 'Discount removed', life: 3000 })
        await load()
      }
      catch (err) {
        toast.add({ severity: 'error', summary: 'Could not remove the discount', detail: err instanceof Error ? err.message : undefined, life: 5000 })
      }
    },
  })
}
</script>
